import { useState } from 'react';
import Button from './Button';
import OfficerDidSelect from './OfficerDidSelect';
import { OFFICE_COUNTRIES } from '../data/officeCountries';
import { useApprovedOfficers } from '../hooks/useApprovedOfficers';
import { parseOfficerDid } from '../web3/applicationEnvelope';
import { persistAndSubmitApplication } from '../web3/persistApplication';
import { ensureSessionProof, notifyApplicationSubmitted } from '../web3/serverApi';
import { fileToBytes, sealApplicationForInsert } from '../web3/sealApplication';
import { useWeb3Auth } from '../web3/Web3AuthContext';
import { MAX_APPLICATION_DOCUMENT_BYTES, type SessionProof } from '../web3/serverContract';
import GetDidCta from './GetDidCta';
import {
  applicationSubmitError,
  submitErrorNeedsDidOnboarding,
} from '../web3/didEligibility';

const AthleteForm = () => {
  const web3 = useWeb3Auth();
  const [formData, setFormData] = useState({
    fullName: '',
    age: '',
    gender: '',
    email: '',
    phone: '',
    country: '',
    otherCountry: '',
    sportsOfficer: '',
    sport: '',
    currentLevel: '',
    achievements: '',
    goals: '',
    motivation: '',
    documents: null as File | null,
    video: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { officers, loading: officersLoading } = useApprovedOfficers(
    formData.country,
    web3.chainId
  );

  const validate = () => {
    const newErrors: Record<string, string> = {};
    
    const requiredFields = [
      'fullName', 'age', 'gender', 'email', 'phone', 'country', 'sportsOfficer', 
      'sport', 'currentLevel', 'achievements', 'goals', 'motivation',
      'video'
    ];
    
    requiredFields.forEach(field => {
      if (!formData[field as keyof typeof formData]) {
        newErrors[field] = 'This field is required';
      }
    });

    if (formData.country === 'Other' && !formData.otherCountry) {
      newErrors.otherCountry = 'Please specify your country';
    }

    if (!formData.documents) {
      newErrors.documents = 'Please upload your documents';
    } else if (formData.documents.size > MAX_APPLICATION_DOCUMENT_BYTES) {
      newErrors.documents = `Documents must be ${Math.round(MAX_APPLICATION_DOCUMENT_BYTES / (1024 * 1024))} MB or smaller`;
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (formData.age && (isNaN(Number(formData.age)) || Number(formData.age) <= 0)) {
      newErrors.age = 'Please enter a valid age';
    }

    if (formData.video && !formData.video.match(/^(https?:\/\/)?(www\.)?(youtube\.com|vimeo\.com)\/.+$/)) {
      newErrors.video = 'Please enter a valid YouTube or Vimeo URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => {
      if (name === 'country') {
        return {
          ...prev,
          [name]: value,
          sportsOfficer: value === 'Other' ? 'Needs Review' : '',
          otherCountry: value === 'Other' ? prev.otherCountry : ''
        };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFormData(prev => ({ ...prev, documents: e.target.files?.[0] || null }));
    }
  };

  const sendTelegramNotification = async (didId: number, session: SessionProof) => {
    try {
      const { sent } = await notifyApplicationSubmitted({ role: 'athlete', didId, session });
      if (!sent) console.error('Telegram bot configuration missing');
    } catch (error) {
      console.error('Failed to send Telegram notification:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      setSubmitting(true);
      try {
        const countryValue = formData.country === 'Other' ? formData.otherCountry : formData.country;
        const files = formData.documents
          ? [
              {
                name: formData.documents.name,
                type: formData.documents.type || 'application/octet-stream',
                bytes: await fileToBytes(formData.documents),
              },
            ]
          : [];
        const sealed = await sealApplicationForInsert({
          role: 'athlete',
          connectedAddress: web3.address,
          files,
          plaintext: {
            full_name: formData.fullName,
            age: parseInt(formData.age, 10),
            gender: formData.gender,
            email: formData.email,
            phone: formData.phone,
            country: countryValue,
            sports_officer_did: parseOfficerDid(formData.sportsOfficer),
            sport: formData.sport,
            current_level: formData.currentLevel,
            achievements: formData.achievements,
            goals: formData.goals,
            motivation: formData.motivation,
            video_url: formData.video,
          },
        });

        const raw = web3.getRawProvider();
        if (!raw || !web3.chainId) {
          throw new Error('Connect your wallet on Apertum to submit on-chain.');
        }
        const session = await ensureSessionProof(web3.signIn);
        await persistAndSubmitApplication({
          sealed,
          role: 'athlete',
          session,
          rawProvider: raw,
          chainId: web3.chainId,
          publicMeta: {
            country: countryValue,
            officerDid: parseOfficerDid(formData.sportsOfficer),
          },
        });

        await sendTelegramNotification(sealed.did_id, session);
        
        setSubmitted(true);
      } catch (error) {
        console.error('Error submitting form:', error);
        setErrors(prev => ({
          ...prev,
          submit: applicationSubmitError(error),
        }));
      } finally {
        setSubmitting(false);
      }
    }
  };

  if (submitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
        <h3 className="text-2xl font-semibold text-green-800 mb-4">
          Thank you for your application!
        </h3>
        <p className="text-green-700 mb-6">
          We will review your application and get back to you soon.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Button
            variant="secondary"
            onClick={() => setSubmitted(false)}
          >
            Submit Another Application
          </Button>
          <Button
            to="/athlete-profile"
            variant="primary"
          >
            View Sample Athlete Profile
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.fullName ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.fullName && <p className="mt-1 text-sm text-red-500">{errors.fullName}</p>}
        </div>

        <div>
          <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-1">
            Age <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            id="age"
            name="age"
            value={formData.age}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.age ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.age && <p className="mt-1 text-sm text-red-500">{errors.age}</p>}
        </div>

        <div>
          <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">
            Gender <span className="text-red-500">*</span>
          </label>
          <select
            id="gender"
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.gender ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
          {errors.gender && <p className="mt-1 text-sm text-red-500">{errors.gender}</p>}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.phone ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
        </div>

        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
            Country <span className="text-red-500">*</span>
          </label>
          <select
            id="country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.country ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select country</option>
            {OFFICE_COUNTRIES.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
            <option value="Other">Other</option>
          </select>
          {errors.country && <p className="mt-1 text-sm text-red-500">{errors.country}</p>}
        </div>

        {formData.country === 'Other' && (
          <div>
            <label htmlFor="otherCountry" className="block text-sm font-medium text-gray-700 mb-1">
              Specify Country <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="otherCountry"
              name="otherCountry"
              value={formData.otherCountry}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.otherCountry ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter your country"
            />
            {errors.otherCountry && <p className="mt-1 text-sm text-red-500">{errors.otherCountry}</p>}
          </div>
        )}

        <OfficerDidSelect
          country={formData.country}
          value={formData.sportsOfficer}
          officers={officers}
          loading={officersLoading}
          error={errors.sportsOfficer}
          onChange={handleChange}
        />

        <div>
          <label htmlFor="sport" className="block text-sm font-medium text-gray-700 mb-1">
            Sport <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="sport"
            name="sport"
            value={formData.sport}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.sport ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.sport && <p className="mt-1 text-sm text-red-500">{errors.sport}</p>}
        </div>

        <div>
          <label htmlFor="currentLevel" className="block text-sm font-medium text-gray-700 mb-1">
            Current Level <span className="text-red-500">*</span>
          </label>
          <select
            id="currentLevel"
            name="currentLevel"
            value={formData.currentLevel}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.currentLevel ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select level</option>
            <option value="Beginner">Beginner</option>
            <option value="Intermediate">Intermediate</option>
            <option value="Advanced">Advanced</option>
            <option value="Professional">Professional</option>
          </select>
          {errors.currentLevel && <p className="mt-1 text-sm text-red-500">{errors.currentLevel}</p>}
        </div>
      </div>

      <div>
        <label htmlFor="achievements" className="block text-sm font-medium text-gray-700 mb-1">
          Achievements <span className="text-red-500">*</span>
          <span className="text-xs text-gray-500 ml-1">
            (Include your results and training intensity)
          </span>
        </label>
        <textarea
          id="achievements"
          name="achievements"
          value={formData.achievements}
          onChange={handleChange}
          rows={4}
          className={`w-full px-4 py-2 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.achievements ? 'border-red-500' : 'border-gray-300'
          }`}
        ></textarea>
        {errors.achievements && <p className="mt-1 text-sm text-red-500">{errors.achievements}</p>}
      </div>

      <div>
        <label htmlFor="goals" className="block text-sm font-medium text-gray-700 mb-1">
          Goals <span className="text-red-500">*</span>
        </label>
        <textarea
          id="goals"
          name="goals"
          value={formData.goals}
          onChange={handleChange}
          rows={4}
          className={`w-full px-4 py-2 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.goals ? 'border-red-500' : 'border-gray-300'
          }`}
        ></textarea>
        {errors.goals && <p className="mt-1 text-sm text-red-500">{errors.goals}</p>}
      </div>

      <div>
        <label htmlFor="motivation" className="block text-sm font-medium text-gray-700 mb-1">
          Motivation Statement <span className="text-red-500">*</span>
          <span className="text-xs text-gray-500 ml-1">
            (Explain why you deserve this support)
          </span>
        </label>
        <textarea
          id="motivation"
          name="motivation"
          value={formData.motivation}
          onChange={handleChange}
          rows={4}
          className={`w-full px-4 py-2 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.motivation ? 'border-red-500' : 'border-gray-300'
          }`}
        ></textarea>
        {errors.motivation && <p className="mt-1 text-sm text-red-500">{errors.motivation}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="documents" className="block text-sm font-medium text-gray-700 mb-1">
            Upload Documents <span className="text-red-500">*</span>
            <span className="text-xs text-gray-500 ml-1">
              (PDFs or images only)
            </span>
          </label>
          <input
            type="file"
            id="documents"
            name="documents"
            onChange={handleFileChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.documents ? 'border-red-500' : 'border-gray-300'
            }`}
            accept=".pdf,.jpg,.jpeg,.png"
          />
          {errors.documents && <p className="mt-1 text-sm text-red-500">{errors.documents}</p>}
        </div>

        <div>
          <label htmlFor="video" className="block text-sm font-medium text-gray-700 mb-1">
            Video Link <span className="text-red-500">*</span>
            <span className="text-xs text-gray-500 ml-1">
              (YouTube or Vimeo URL)
            </span>
          </label>
          <input
            type="text"
            id="video"
            name="video"
            value={formData.video}
            onChange={handleChange}
            placeholder="https://youtube.com/... or https://vimeo.com/..."
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.video ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.video && <p className="mt-1 text-sm text-red-500">{errors.video}</p>}
        </div>
      </div>

      {errors.submit && (
        <div className="space-y-3">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {errors.submit}
          </div>
          {submitErrorNeedsDidOnboarding(errors.submit) && (
            <GetDidCta compact reason="missing_did" message={errors.submit} />
          )}
        </div>
      )}

      <div className="pt-4">
        <Button 
          type="submit" 
          variant="primary" 
          size="lg" 
          className="w-full"
          disabled={submitting}
        >
          {submitting ? 'Submitting...' : 'Submit Application'}
        </Button>
      </div>
    </form>
  );
};

export default AthleteForm;